import clsx, { ClassValue } from 'clsx';
import has from 'lodash/has';
import isPlainObject from 'lodash/isPlainObject';
import {
  Attributes,
  ComponentProps as BaseComponentProps,
  ComponentType as BaseComponentType,
  PropsWithChildren,
  createElement,
  useMemo,
  type JSX,
} from 'react';
import { DetailedHTMLFactory, ReactHTML } from 'react-html';
import { extendTailwindMerge } from 'tailwind-merge';

// Extend tailwind merge to add custom class groups
const twMerge = extendTailwindMerge({
  classGroups: {
    animate: [{ animate: ['wiggle'] }],
    shadow: [{ shadow: ['button', 'card', 'medium', 'snackbar'] }],
    z: [{ z: ['backdrop', 'dialog', 'dropdown', 'popover', 'tooltip'] }],
  },
  theme: {
    colors: [
      'overlay',
      {
        black: ['charcoal', ''],
        blue: ['dark', '', 'highlight', 'hover', 'light'],
        green: ['brown', 'dark', '', 'light'],
        grey: ['border', '', 'disabled', 'input-border', 'input-disabled', 'light'],
        orange: ['dark', ''],
        pink: ['dark', ''],
        red: ['deep', '', 'hover', 'light'],
        white: ['', 'green', 'snow', 'teal', 'yellow'],
        yellow: ['', 'light'],
      },
    ],
  },
});

/**
 * Combine basic class merging utility with basic tailwind merging utility
 *
 * @param classes
 * @returns
 */
export function clsxm(...classes: ClassValue[]) {
  return twMerge(clsx(...classes));
}

export type SafeClassValue = SafeClassValue[] | string | number | null | boolean | undefined;

export type StyledProps = PropsWithChildren<{ className?: string }>;

export type StyledPropsParamType<Props> =
  | Props
  | SafeClassValue
  | ((props: Props) => Props | SafeClassValue);

export type ExtractHTMLElementByType<HTMLType> = HTMLType extends DetailedHTMLFactory<
  // NOTE: the first argument could be anything
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any,
  infer ElementType
>
  ? ElementType
  : HTMLType;

/**
 * Component creation utility with class name merging using clsxm
 *
 * @param component
 * @param passedProps
 * @returns
 */
export function styled<
  Props extends object & StyledProps,
  ComponentType extends keyof ReactHTML | BaseComponentType = keyof ReactHTML | BaseComponentType,
  ComponentProps = ComponentType extends BaseComponentType<Props>
    ? BaseComponentProps<ComponentType>
    : ComponentType extends keyof ReactHTML
    ? JSX.IntrinsicElements[ComponentType]
    : unknown,
>(
  component: ComponentType,
  passedProps?: StyledPropsParamType<ComponentProps & Props>,
  ...classes: ClassValue[]
) {
  type TargetProps = ComponentProps & Props;

  function Component({ className, ...props }: TargetProps) {
    const propsParams = useMemo(
      () =>
        typeof passedProps === 'function'
          ? passedProps({ ...props, className: clsx(className) } as TargetProps)
          : passedProps,
      [props, className],
    );

    const classNameParam = useMemo(() => {
      if (!isPlainObject(propsParams)) return propsParams;

      if (has(propsParams, 'className')) {
        return (propsParams as TargetProps)?.className;
      }

      return undefined;
    }, [propsParams]);

    return createElement(
      component,
      {
        ...(isPlainObject(propsParams) && (propsParams as TargetProps)),
        ...props,
        className: clsxm(classNameParam, ...classes, className),
      } as Attributes,
      props.children,
    );
  }

  let displayName = 'Anonymous';
  if (typeof component === 'object' && component && 'displayName' in component) {
    displayName = (component as { displayName: string }).displayName;
  }
  Component.displayName = `Styled${displayName ?? 'Anonymous'}`;

  return Component;
}
